import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { useRole } from '../context/RoleContext';
import RoleToggle from './RoleToggle';
import {
  Bell,
  BookOpen,
  BarChart3,
  Trophy,
  LogOut,
  ShieldCheck,
  X,
  MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Navigation Context to share active nav state and notification functions
const NavigationContext = createContext();

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export const NavigationProvider = ({ children }) => {
  const [activeNav, setActiveNav] = useState('chat');
  const [lastSeenNotifications, setLastSeenNotifications] = useState(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem('lastSeenNotifications');
    return stored ? JSON.parse(stored) : { student: null, professor: null };
  });

  // Mark notifications as seen for a specific role
  const markNotificationsSeen = useCallback((isProfessor) => {
    const now = new Date().toISOString();
    const key = isProfessor ? 'professor' : 'student';
    setLastSeenNotifications(prev => {
      const updated = { ...prev, [key]: now };
      localStorage.setItem('lastSeenNotifications', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Get last seen timestamp for a role
  const getLastSeenTimestamp = useCallback((isProfessor) => {
    const key = isProfessor ? 'professor' : 'student';
    return lastSeenNotifications[key];
  }, [lastSeenNotifications]);
  
  return (
    <NavigationContext.Provider value={{ 
      activeNav, 
      setActiveNav, 
      markNotificationsSeen,
      getLastSeenTimestamp,
      lastSeenNotifications
    }}>
      {children}
    </NavigationContext.Provider>
  );
};

// Navigation items for Student (badge will be set dynamically)
const studentNavItems = [
  { id: 'chat', label: 'Chat', icon: MessageSquare },
  { id: 'notifications', label: 'Notificaciones', icon: Bell, badge: null },
  { id: 'challenges', label: 'Retos', icon: Trophy },
  { id: 'results', label: 'Resultados', icon: BarChart3 },
];

// Navigation items for Professor (badge will be set dynamically)
const professorNavItems = [
  { id: 'notifications', label: 'Notificaciones', icon: Bell, badge: null },
  { id: 'courses', label: 'Mis Clases', icon: BookOpen },
  { id: 'create-challenges', label: 'Crear Retos', icon: Trophy },
  { id: 'results', label: 'Resultados', icon: BarChart3 },
  { id: 'fact-check', label: 'Fact Check™', icon: ShieldCheck },
];

const LeftSidebar = ({ isOpen, onClose }) => {
  const { isProfessor } = useRole();
  const { activeNav, setActiveNav, getLastSeenTimestamp, lastSeenNotifications } = useNavigation();
  const [notificationCount, setNotificationCount] = useState(0);

  // Fetch notification count (only unread/new notifications)
  useEffect(() => {
    const fetchNotificationCount = async () => {
      try {
        const endpoint = isProfessor 
          ? `${API_URL}/api/notifications/professor`
          : `${API_URL}/api/notifications/student/s2a1`;
        
        const response = await fetch(endpoint);
        if (response.ok) {
          const data = await response.json();
          const notifications = data.notifications || [];
          
          // Get last seen timestamp for current role
          const lastSeen = getLastSeenTimestamp(isProfessor);
          
          if (!lastSeen) {
            // User has never seen notifications, show all count
            setNotificationCount(notifications.length);
          } else {
            // Count only notifications newer than lastSeen
            const lastSeenDate = new Date(lastSeen);
            const unreadCount = notifications.filter(n => {
              const notifDate = new Date(n.created_at);
              return notifDate > lastSeenDate;
            }).length;
            setNotificationCount(unreadCount);
          }
        }
      } catch (err) {
        console.error('Error fetching notification count:', err);
      }
    };

    fetchNotificationCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchNotificationCount, 30000);
    return () => clearInterval(interval);
  }, [isProfessor, getLastSeenTimestamp, lastSeenNotifications]);

  // Get nav items with dynamic notification badge
  const getNavItems = () => {
    const items = isProfessor ? professorNavItems : studentNavItems;
    return items.map(item => {
      if (item.id === 'notifications') {
        return { ...item, badge: notificationCount > 0 ? notificationCount : null };
      }
      return item;
    });
  };

  const navItems = getNavItems();

  const handleNavClick = (itemId) => {
    // For notifications
    if (itemId === 'notifications') {
      setActiveNav('notifications');
    }
    // For Chat in student mode
    else if (itemId === 'chat') {
      setActiveNav('chat');
    }
    // For "Mis Clases" (courses) in Professor mode, show the config view
    else if (itemId === 'courses' && isProfessor) {
      setActiveNav('mis-clases');
    } else if (itemId === 'courses' && !isProfessor) {
      // For student, just set active but show chat (placeholder)
      setActiveNav('chat');
    } else if (itemId === 'create-challenges' && isProfessor) {
      // For "Crear Retos" in Professor mode
      setActiveNav('crear-retos');
    } else if (itemId === 'results' && isProfessor) {
      // For "Resultados" in Professor mode
      setActiveNav('profesor-resultados');
    } else if (itemId === 'results' && !isProfessor) {
      // For "Resultados" in Student mode
      setActiveNav('student-resultados');
    } else if (itemId === 'challenges' && !isProfessor) {
      // For "Retos" in Student mode
      setActiveNav('student-retos');
    } else if (itemId === 'fact-check' && isProfessor) {
      // For "Fact Check" in Professor mode
      setActiveNav('fact-check');
    } else {
      // For other items, show chat for now (placeholder)
      setActiveNav('chat');
    }
    
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  // Determine which nav item is visually active
  const getIsActive = (itemId) => {
    if (itemId === 'chat' && activeNav === 'chat') {
      return true;
    }
    if (itemId === 'notifications' && activeNav === 'notifications') {
      return true;
    }
    if (itemId === 'courses' && isProfessor && activeNav === 'mis-clases') {
      return true;
    }
    if (itemId === 'create-challenges' && isProfessor && activeNav === 'crear-retos') {
      return true;
    }
    if (itemId === 'results' && isProfessor && activeNav === 'profesor-resultados') {
      return true;
    }
    if (itemId === 'results' && !isProfessor && activeNav === 'student-resultados') {
      return true;
    }
    if (itemId === 'challenges' && !isProfessor && activeNav === 'student-retos') {
      return true;
    }
    if (itemId === 'fact-check' && isProfessor && activeNav === 'fact-check') {
      return true;
    }
    return false;
  };

  return (
    <aside 
      className={`left-sidebar ${isOpen ? 'open' : ''} ${isProfessor ? 'dark-mode' : ''}`} 
      data-testid="left-sidebar"
    >
      {/* Close button for mobile */}
      <button
        className="absolute top-4 right-4 md:hidden p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
        onClick={onClose}
        aria-label="Close sidebar"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Logo */}
      <div className="flex items-center gap-2 px-2 mb-6" data-testid="logo">
        <img 
          src="https://customer-assets.emergentagent.com/job_elegant-assistant-1/artifacts/p78l5ov2_milo_no_bg.png" 
          alt="Milo" 
          className="h-[52px] object-contain"
        />
        {isProfessor && (
          <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full ml-auto">PRO</span>
        )}
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 space-y-1" data-testid="main-nav">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = getIsActive(item.id);
          
          return (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                isActive
                  ? isProfessor 
                    ? 'nav-item-active-dark' 
                    : 'nav-item-active'
                  : isProfessor
                    ? 'text-gray-400 hover:bg-white/10 hover:text-white'
                    : 'text-gray-600 hover:bg-white/50 hover:text-gray-900'
              } ${isActive ? 'font-medium' : ''}`}
              data-testid={`nav-${item.id}`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-sm">{item.label}</span>
              {item.badge && (
                <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full ${
                  isProfessor ? 'bg-blue-500 text-white' : 'bg-gray-900 text-white'
                }`}>
                  {item.badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Logout Button */}
      <button
        className={`flex items-center gap-2 px-3 py-2 mt-auto text-xs transition-colors ${
          isProfessor 
            ? 'text-gray-500 hover:text-red-400' 
            : 'text-gray-400 hover:text-red-500'
        }`}
        data-testid="logout-btn"
      >
        <LogOut className="w-4 h-4" />
        <span>Cerrar sesión</span>
      </button>

      {/* Role Toggle */}
      <RoleToggle />
    </aside>
  );
};

export default LeftSidebar;
